import React, { useEffect, useState, useRef } from 'react';
import { companyInfo, images } from '../data/mock';
import { Phone, ArrowDown } from 'lucide-react';

const PARALLAX_FACTOR = 0.9;
const PARALLAX_FACTOR_MOBILE = 0.9;

const Hero = () => {
  const [scrollY, setScrollY] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const rafRef = useRef(null);

  useEffect(() => {
    const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mqMobile = window.matchMedia('(max-width: 768px)');
    setPrefersReducedMotion(mqReduced.matches);
    setIsMobile(mqMobile.matches);
    const handleResize = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    const handleChangeReduced = (e) => setPrefersReducedMotion(e.matches);
    mqReduced.addEventListener('change', handleChangeReduced);
    mqMobile.addEventListener('change', handleResize);
    return () => {
      mqReduced.removeEventListener('change', handleChangeReduced);
      mqMobile.removeEventListener('change', handleResize);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        rafRef.current = null;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const parallaxEnabled = !prefersReducedMotion;
  const factor = isMobile ? PARALLAX_FACTOR_MOBILE : PARALLAX_FACTOR;
  const parallaxOffset = parallaxEnabled ? scrollY * factor : 0;

  return (
    <section id="home" className="relative w-full overflow-hidden bg-[#1E4D2B] min-h-[75vh] sm:min-h-[80vh] md:min-h-0">
      {/* Spacer: auf Desktop Höhe vom Bild, auf Mobile/Tablet ignoriert (min-height) */}
      <img
        src={images.hero}
        alt=""
        aria-hidden="true"
        className="hidden md:block w-full h-auto pointer-events-none invisible"
      />
      {/* Parallax-Layer: Mobile/Tablet Bild füllt Bereich, Desktop Originalmaß */}
      <div
        className="absolute inset-0 w-full overflow-hidden"
        style={{
          transform: `translate3d(0, ${parallaxOffset}px, 0)`,
          willChange: parallaxEnabled ? 'transform' : 'auto',
        }}
      >
        <img
          src={images.hero}
          alt="Hausmeisterservice"
          className="w-full h-full min-h-full object-cover object-center block md:min-h-0 md:h-auto md:[object-fit:initial]"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1E4D2B]/85 via-[#1E4D2B]/75 to-[#1E4D2B]/90" />
      </div>

      {/* Content - Absolute overlay, responsive padding */}
      <div className="absolute inset-0 flex items-center justify-center container mx-auto px-4 sm:px-6 md:px-8 pt-24 pb-10 sm:pt-32 sm:pb-14 md:pt-44 md:pb-20 text-center">
        <div className="max-w-4xl mx-auto w-full">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full mb-3 sm:mb-6 md:mb-8 border border-white/20">
            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#FFD700] rounded-full animate-pulse shrink-0" />
            <span className="text-white/90 text-[10px] sm:text-xs md:text-sm font-medium">Ihr Partner in Frankfurt & Kahl am Main</span>
          </div>

          {/* Headline - bessere Abstufung für Mobile/Tablet */}
          <h1 className="text-[1.75rem] xs:text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-3 sm:mb-5 md:mb-6 leading-[1.15] sm:leading-tight tracking-tight">
            {companyInfo.name}
            <span className="block text-[#FFD700] mt-1 sm:mt-2 md:mt-2.5">{companyInfo.tagline}</span>
          </h1>

          {/* Subheadline - lesbar auf kleinen Screens */}
          <p className="text-xs sm:text-base md:text-lg lg:text-xl text-white/85 mb-5 sm:mb-8 md:mb-10 max-w-2xl mx-auto leading-relaxed sm:leading-relaxed px-0 sm:px-2">
            {companyInfo.subline}
          </p>

          {/* CTA Buttons - auf Mobile untereinander, bessere Touch-Ziele */}
          <div className="flex flex-row items-center justify-center gap-2 sm:gap-4 mb-6 sm:mb-10 md:mb-12 max-w-md sm:max-w-none mx-auto flex-wrap">
            <button
              onClick={() => scrollToSection('kontakt')}
              className="flex-1 min-w-0 sm:flex-none bg-[#FFD700] text-[#1E4D2B] px-3 py-3 sm:px-8 sm:py-4 rounded-full font-semibold text-[11px] sm:text-base md:text-lg leading-tight hover:bg-[#FFE44D] transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#FFD700]/30 sm:min-w-[220px] md:min-w-[240px] touch-manipulation"
            >
              Jetzt Kontakt aufnehmen
            </button>
            <a
              href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
              className="flex-1 min-w-0 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-3 bg-white/10 backdrop-blur-sm text-white px-3 py-3 sm:px-8 sm:py-4 rounded-full font-semibold text-[11px] sm:text-base md:text-lg leading-tight border-2 border-white/30 hover:bg-white/20 transition-all duration-200 hover:-translate-y-1 sm:min-w-[220px] md:min-w-[240px] touch-manipulation"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
              <span>{companyInfo.phone}</span>
            </a>
          </div>

          {/* Scroll Indicator - ab sm sichtbar */}
          <button
            onClick={() => scrollToSection('leistungen')}
            className="inline-flex flex-col items-center gap-1.5 text-white/60 hover:text-white transition-colors mt-2 sm:mt-4 hidden sm:inline-flex touch-manipulation"
          >
            <span className="text-xs sm:text-sm">Mehr erfahren</span>
            <ArrowDown className="w-4 h-4 sm:w-5 sm:h-5 animate-bounce" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
